import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import IBFilters from "./ibFilters";
import CalculationNav from "./calculationNav";
import "./styles.css";
import CalculationGrid from "./calculationPagesGrid";
import {
  getDataWAuth,
  postDataWAuth,
  putDataWAuth,
} from "../../utils/api-service";
import {
  APP_ACTIONS,
  IB_CALC_SCHEDULER_STATUS,
  IB_CALCULATION_EXCLUDE,
  IB_CALCULATION_GETDATA,
  IB_CALCULATION_INCLUDE,
  IB_PDF_DOWNLOAD,
  IB_CALCULATION_REASONCODES,
  IB_CALCULATION_SAVE,
  IB_CHECKED_CALCULATION,
  IB_POST_CALCULATION,
  IB_LOGS_DOWNLOAD,
  IB_All_DOWNLOAD,
  IB_SAP_CALLBY_ASSIGNMENT,
} from "../../constants/constant";
import ToastMessageFunction from "../../common/toast-message-function";
import ModalMessageFunction from "../../common/modal-message-function";
import { checkErrorMsg } from "../../utils/utils";
import { useTranslation } from "../../common/translation-context";
import PageLoader from "../../common/PageLoader";
import dayjs from "dayjs";

function CalculationPage() {
  const location = useLocation();
  const activeTabFromState = location?.state?.activeTab;
  
  const [country, setCountry] = useState("");
  const [fiscalMonth, setFiscalMonth] = useState("");
  const [requestedPage, setRequestedPage] = useState(activeTabFromState || "bp_check");
  const [data, setData] = useState([]);
  const [reasonCodes, setReasonCodes] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState({});
  const [isLoaderVisible, setIsLoaderVisible] = useState();
  const { translate: t } = useTranslation();
  const toastManager = ToastMessageFunction();
  const showToast = useCallback(
    (message, severity) => {
      toastManager.getToast(message, severity);
    },
    [toastManager],
  );
  const [actionPerformed, setActionPerformed] = useState(false);
  const [schedulerStatus, setSchedulerStatus] = useState();
  const [isBookingActive, setIsBookingActive] = useState(false);
  const [lastExpandedRowId, setLastExpandedRowId] = useState(null);
  const pollingTimerRef = useRef(null); // 30-second grid refresh polling
  const statusPollingRef = useRef(null); // 3-second status polling
  const processAction = (action) => {
    setActionPerformed(action);
  };
  const modalManager = ModalMessageFunction(processAction);

  const getModalAction = (requestType) => {
    modalManager.getModal(requestType);
  };

  const getSelectedFilters = (country, fiscalMonth) => {
    setCountry(country);
    setFiscalMonth(fiscalMonth);
  };

  const getActivePage = (page) => {
    setRequestedPage(page);
  };

  const getMinMaxStartEndDate = (requestList) => {
    if (!requestList || requestList.length === 0) {
      return { minStartDate: null, maxEndDate: null };
    }

    let minStartDate = null;
    let maxEndDate = null;

    requestList.forEach((item) => {
      if (item.calculatedInterest && item.calculatedInterest !== 0) {
        const startDate = item.startDate ? dayjs(item.startDate) : null;
        const endDate = item.endDate ? dayjs(item.endDate) : null;

        if (startDate && (!minStartDate || startDate.isBefore(minStartDate))) {
          minStartDate = startDate;
        }
        if (endDate && (!maxEndDate || endDate.isAfter(maxEndDate))) {
          maxEndDate = endDate;
        }
      }
    });

    return { minStartDate, maxEndDate };
  };

  const addTotalNoOfDays = (data) => {
    const { minStartDate, maxEndDate } = getMinMaxStartEndDate(
      data.ibRequestCalculationDTOList,
    );
    return data.totalInterest &&
      data.totalInterest !== 0 &&
      minStartDate &&
      maxEndDate
      ? maxEndDate.diff(minStartDate, "days")
      : 0;
  };

  const generateActivePageData = (data) => {
    return data.ibRequestCalculationWrapperList
      .sort(
        (a, b) =>
          a.customerNo.localeCompare(b.customerNo) ||
          a.assignmentNo.localeCompare(b.assignmentNo),
      )
      .map((row) => {
        if (row.ibRequestCalculationDTOList?.length > 0) {
          row.ibRequestCalculationDTOList = row.ibRequestCalculationDTOList
            .slice()
            .sort((a, b) => {
              const amountA =
                a.debitCreditIndicator === "H"
                  ? -a.amountInDocCurrency
                  : a.amountInDocCurrency;
              const amountB =
                b.debitCreditIndicator === "H"
                  ? -b.amountInDocCurrency
                  : b.amountInDocCurrency;
              return amountB - amountA;
            });
        }
        row.totalNoOfDays = addTotalNoOfDays(row);
        return row;
      });
  };

  const getActivePageData = () => {
    if (country && fiscalMonth && requestedPage && !schedulerStatus) {
      setData([]);
      setIsLoaderVisible(true);
      getDataWAuth(IB_CALCULATION_GETDATA, {
        requestedPage,
        country,
        fiscalMonthYear: fiscalMonth,
      })
        .then((response) => {
          if (response.status === 200) {
            if (response.data.calculationSchedulerStatus) {
              isSchedulerRunning(true);
            }
            setData(generateActivePageData(response.data));
            setItemsPerPage(response.data.ibCountDTO);
            setIsBookingActive(response.data.isBookingActive);
          } else {
            showToast(t("failedToLoad"), "error");
          }
          setIsLoaderVisible(false);
        })
        .catch((error) => {
          console.error(error);
          showToast(checkErrorMsg(error) || t("failedToLoad"), "error");
          setIsLoaderVisible(false);
        });
    }
  };

  useEffect(() => {
    if (isBookingActive) {
      pollingTimerRef.current = setInterval(() => {
        getActivePageData();
      }, 30000);
    } else {
      // Clear 30-second grid refresh polling
      if (pollingTimerRef.current) {
        clearInterval(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
      // Also clear status polling when booking is no longer active
      if (statusPollingRef.current) {
        clearInterval(statusPollingRef.current);
        statusPollingRef.current = null;
      }
    }

    return () => {
      if (pollingTimerRef.current) {
        clearInterval(pollingTimerRef.current);
      }
      if (statusPollingRef.current) {
        clearInterval(statusPollingRef.current);
      }
    };
  }, [isBookingActive, requestedPage]);

  const getReasonCodes = () => {
    if (country && fiscalMonth) {
      getDataWAuth(IB_CALCULATION_REASONCODES, {
        country,
        fisMonthYear: fiscalMonth,
      })
        .then((response) => {
          if (response.status === 200) {
            setReasonCodes(response.data);
          } else {
            showToast(t("failedToLoad"), "error");
          }
        })
        .catch((error) => {
          console.error(error);
          showToast(checkErrorMsg(error) || t("failedToLoad"), "error");
        });
    }
  };

  useEffect(() => {
    if (!schedulerStatus) getActivePageData();
  }, [country, fiscalMonth, requestedPage]);

  const save = (savedData, editedRow) => {
    const mainRowId = savedData.assignmentNo;
    const updatedInterestRow =
      editedRow &&
      editedRow.find((row) => savedData.assignmentNo === row.assignmentNo);
    setIsLoaderVisible(true);
    putDataWAuth(IB_CALCULATION_SAVE, savedData)
      .then((response) => {
        if (response.status === 200) {
          setLastExpandedRowId(savedData.id);
          showToast(t("saveSuccess"), "success");
          setData((prevData) =>
            prevData.map((row) => {
              if (row.assignmentNo === mainRowId) {
                const updatedRow = { ...row };
                const updatedRequestList =
                  updatedRow.ibRequestCalculationDTOList.map((item) => {
                    if (item.id === savedData.id) {
                      return { ...item, ...savedData };
                    }
                    return item;
                  });
                updatedRow.ibRequestCalculationDTOList = updatedRequestList;
                updatedRow.totalInterest = updatedInterestRow.totalInterest;
                return updatedRow;
              }
              return row;
            }),
          );
        } else {
          showToast(t("saveError"), "error");
        }
        setIsLoaderVisible(false);
      })
      .catch((error) => {
        console.error(error);
        showToast(checkErrorMsg(error) || t("somethingWentWrong"), "error");
        setIsLoaderVisible(false);
      });
  };

  const handleExcludeClick = (row) => {
    setIsLoaderVisible(true);
    const listOfIDs = row.ibRequestCalculationDTOList.map((item) => item.id);

    putDataWAuth(IB_CALCULATION_EXCLUDE, listOfIDs, { requestedPage })
      .then((response) => {
        if (response.status === 200) {
          showToast(t("excludeSuccess"), "success");
          getActivePageData();
        } else {
          showToast(t("excludeError"), "error");
        }
        setIsLoaderVisible(false);
      })
      .catch((error) => {
        console.error(error);
        showToast(checkErrorMsg(error) || t("excludeError"), "error");
        setIsLoaderVisible(false);
      });
  };

  const handleIncludeClick = (row) => {
    setIsLoaderVisible(true);
    const listOfIDs = row.ibRequestCalculationDTOList.map((item) => item.id);

    putDataWAuth(IB_CALCULATION_INCLUDE, listOfIDs, {
      requestedPage,
      totalInterest: row.totalInterest,
    })
      .then((response) => {
        if (response.status === 200) {
          showToast(t("includeSuccess"), "success");
          getActivePageData();
        } else {
          showToast(t("includeError"), "error");
        }
        setIsLoaderVisible(false);
      })
      .catch((error) => {
        console.error(error);
        showToast(checkErrorMsg(error) || t("includeError"), "error");
        setIsLoaderVisible(false);
      });
  };

  const handleDownloadPDF = (row) => {
    const data = {
      requestStatus: row.status,
      customerNo: row.customerNo,
      assignmentNo: row.assignmentNo,
      totalInterestAmount: row.totalInterest,
      currencyKey: row.currencyKey,
      businessArea: row.businessArea,
      accountingDocNo: row.debitNoteNumber,
      country: row.country,
    };
    postDataWAuth(IB_PDF_DOWNLOAD, data, { fisMonthYear: fiscalMonth })
      .then((response) => {
        if (response.status === 200) {
          window.open(response.data);
        } else {
          showToast(t("downloadError"), "error");
        }
      })
      .catch((error) => {
        console.error(error);
        showToast(checkErrorMsg(error) || t("downloadError"), "error");
      });
  };

  const handleCheckboxClick = (row) => {
    const listOfIDs = row.ibRequestCalculationDTOList.map((item) => item.id);

    putDataWAuth(IB_CHECKED_CALCULATION, listOfIDs, {
      bpChecked: row.bpChecked,
    })
      .then((response) => {
        if (response.status !== 200) {
          showToast(t("somethingWentWrong"), "error");
        }
      })
      .catch((error) => {
        console.error(error);
        showToast(checkErrorMsg(error) || t("somethingWentWrong"), "error");
      });
  };

  const handleOnBookInterstClick = () => {
    getModalAction(APP_ACTIONS.BOOKINTEREST);
  };

  const generateBookInterest = () => {
    return data.map((item) => ({
      requestStatus: item.status,
      customerNo: item.customerNo,
      assignmentNo: item.assignmentNo,
      totalInterestAmount: item.totalInterest,
      currencyKey: item.currencyKey,
      businessArea: item.businessArea,
      country: item.country,
    }));
  };

  const generateCancelDebitNote = (row) => {
    return [
      {
        requestStatus: row.status,
        customerNo: row.customerNo,
        assignmentNo: row.assignmentNo,
        totalInterestAmount: row.totalInterest,
        currencyKey: row.currencyKey,
        businessArea: row.businessArea,
        country: row.country,
        debitNoteNumber: row.debitNoteNumber,
      },
    ];
  };

  useEffect(() => {
    if (schedulerStatus) {
      let intervalId;
      const getStatus = () => {
        getDataWAuth(IB_CALC_SCHEDULER_STATUS, { fisMonthYear: fiscalMonth })
          .then((response) => {
            if (response.status === 200 && response.data === false) {
              setSchedulerStatus(false);
              clearInterval(intervalId);
              getActivePageData();
            }
          })
          .catch((error) => {
            console.error(error);
            clearInterval(intervalId);
          });
      };

      intervalId = setInterval(getStatus, 5000);

      return () => {
        clearInterval(intervalId);
      };
    } else if (fiscalMonth && country) {
      getActivePageData();
    }
  }, [schedulerStatus]);

  const isSchedulerRunning = (schedulerStatus) => {
    setSchedulerStatus(schedulerStatus);
  };

  useEffect(() => {
    if (actionPerformed) {
      setIsLoaderVisible(true);
      const isMultithreadingSupported = country === "IT" || country === "JD";

      if (isMultithreadingSupported) {
        postDataWAuth(IB_POST_CALCULATION + "Async", generateBookInterest(), {
          fisMonthYear: fiscalMonth,
          cancelDebit: false,
        })
          .then((response) => {
            if (!response || (response.status !== 202 && response.status !== 200)) {
              console.error("Invalid or unsuccessful response:", response);
              showToast(t("bookInterestError") || "Failed to start book interest process", "error");
              setIsLoaderVisible(false);
              setIsBookingActive(false);
              return;
            }

            let jobId;
            
            if (response.data && response.data.message && typeof response.data.message === "string" && response.data.message.includes("JobId:")) {
              jobId = response.data.message.split(": ")[1];
            } 

            else if (response.message && typeof response.message === "string" && response.message.includes("JobId:")) {
              jobId = response.message.split(": ")[1];
            } 

            else if (response.data && response.data.data && typeof response.data.data === "string" && response.data.data.includes("JobId:")) {
              jobId = response.data.data.split(": ")[1];
            } 
  
            else if (response.data && response.data.jobId) {
              jobId = response.data.jobId;
            } 
            else if (response.jobId) {
              jobId = response.jobId;
            } 
            else {
              console.error("Unable to extract jobId from response:", JSON.stringify(response));
              showToast(t("bookInterestError") || "Invalid response format - no jobId found", "error");
              setIsLoaderVisible(false);
              setIsBookingActive(false);
              return;
            }

            if (!jobId || jobId.trim() === "") {
              console.error("Extracted jobId is empty or invalid:", jobId);
              showToast(t("bookInterestError") || "Failed to get job ID", "error");
              setIsLoaderVisible(false);
              setIsBookingActive(false);
              return;
            }

            setIsBookingActive(true);
            setIsLoaderVisible(false); // Hide loader as async process has started
            showToast(t("bookInterestStarted") || "Book interest processing started. Progress will update automatically.", "success");
            
            // Start polling for progress
            try {
              startBookingProgressPolling(jobId);
            } catch (pollingError) {
              setIsBookingActive(false);
              setIsLoaderVisible(false);
              showToast(t("bookInterestError") || "Failed to start progress tracking", "error");
            }
          })
          .catch((error) => {
            setIsBookingActive(false);
            setIsLoaderVisible(false);
            
            showToast(checkErrorMsg(error) || t("bookInterestError") || "Failed to start booking", "error");
          });
      } else {
        // Use synchronous endpoint for other countries (DE, ES, FR, GB, IE, PL, PT)
        postDataWAuth(IB_POST_CALCULATION, generateBookInterest(), {
          fisMonthYear: fiscalMonth,
          cancelDebit: false,
        })
          .then((response) => {
            setIsLoaderVisible(false);
            if (response.status === 200) {
              showToast(t("bookInterestSuccess") || "Book interest completed successfully!", "success");
              getActivePageData();
            } else {
              showToast(t("bookInterestError") || "Failed to book interest", "error");
            }
          })
          .catch((error) => {
            setIsLoaderVisible(false);
            showToast(checkErrorMsg(error) || t("bookInterestError") || "Failed to book interest", "error");
          });
      }

      setActionPerformed(false);
    }
  }, [actionPerformed]);

  // Poll for booking progress
  const startBookingProgressPolling = (jobId) => {
    // Clear any existing status polling
    if (statusPollingRef.current) {
      clearInterval(statusPollingRef.current);
      statusPollingRef.current = null;
    }
    
    let pollCount = 0;
    const maxPolls = 600; // 3 seconds * 600 = 30 minutes max
    
    statusPollingRef.current = setInterval(() => {
      pollCount++;
      
      // Stop polling after max attempts
      if (pollCount > maxPolls) {
        clearInterval(statusPollingRef.current);
        statusPollingRef.current = null;
        setIsLoaderVisible(false);
        setIsBookingActive(false);
        showToast("Booking process timeout. Please check status manually.", "warning");
        return;
      }
      
      getDataWAuth(IB_POST_CALCULATION + "/status/" + jobId, {})
        .then((response) => {
          // Add null check
          if (!response) {
            console.error("Received null response from status endpoint");
            return;
          }

          const progressData = response;

          if (!progressData || progressData.progress === undefined) {
            console.error("Invalid progress data in response:", response);
            // Stop polling if we keep getting invalid responses
            if (pollCount > 5) {
              clearInterval(statusPollingRef.current);
              statusPollingRef.current = null;
              setIsBookingActive(false);
            }
            return;
          }

          const progress = progressData.progress;
          const bookingStatus = progressData.status; // "PROCESSING", "COMPLETED", "FAILED", "PARTIAL"

          // Update UI with progress - show toast only occasionally to avoid spam
          if (bookingStatus === "PROCESSING" && progress >= 0 && progress < 100) {
            // Only show toast every 10% progress
            if (progress % 10 === 0 || !progressData.lastToastProgress || progress - progressData.lastToastProgress >= 10) {
              showToast(`Processing: ${progress}% (${progressData.processedAssignments}/${progressData.totalAssignments})`, "info");
              progressData.lastToastProgress = progress;
            }
          }

          // Check if completed - STOP POLLING
          if (bookingStatus === "COMPLETED") {
            clearInterval(statusPollingRef.current);
            statusPollingRef.current = null;
            setIsLoaderVisible(false);
            setIsBookingActive(false);
            showToast(t("bookInterestSuccess") || "Book interest completed successfully!", "success");
            getActivePageData();
          } else if (bookingStatus === "FAILED") {
            clearInterval(statusPollingRef.current);
            statusPollingRef.current = null;
            setIsLoaderVisible(false);
            setIsBookingActive(false);
            showToast(progressData.errorMessage || t("bookInterestError"), "error");
            getActivePageData();
          } else if (bookingStatus === "PARTIAL") {
            clearInterval(statusPollingRef.current);
            statusPollingRef.current = null;
            setIsLoaderVisible(false);
            setIsBookingActive(false);
            showToast(progressData.errorMessage || "Book interest completed with some failures", "warning");
            getActivePageData();
          }
        })
        .catch((error) => {
          console.error("Error checking booking progress:", error);
          clearInterval(statusPollingRef.current);
          statusPollingRef.current = null;
          setIsLoaderVisible(false);
          setIsBookingActive(false);
          showToast(t("bookInterestError"), "error");
        });
    }, 3000); // Poll every 3 seconds
  };

  useEffect(() => {
    if (country && fiscalMonth && !schedulerStatus) {
      getReasonCodes();
    }
  }, [country, fiscalMonth]);

  const handleDownloadLogsClick = () => {
    getDataWAuth(IB_LOGS_DOWNLOAD, { country, fiscalMonthYear: fiscalMonth })
      .then((response) => {
        if (response.status === 200) {
          downloadFile(response.data);
        } else {
          showToast(t("downloadError"), "Something went wrong!!!");
        }
      })
      .catch((error) => {
        console.error(error);
        showToast(checkErrorMsg(error) || t("downloadError"), "error");
      });
  };

  const handleDownloadAllLogsClick = () => {
    getDataWAuth(IB_All_DOWNLOAD, { country, fiscalMonthYear: fiscalMonth })
      .then((response) => {
        if (response.status === 200) {
          downloadFile(response.data);
        } else {
          showToast(t("downloadError"), "Something went wrong!!!");
        }
      })
      .catch((error) => {
        console.error(error);
        showToast(checkErrorMsg(error) || t("downloadError"), "error");
      });
  };

  const handleCancelDebitNote = (row) => {
    setIsLoaderVisible(true);

    postDataWAuth(IB_POST_CALCULATION, generateCancelDebitNote(row), {
      fisMonthYear: fiscalMonth,
      cancelDebit: true,
    })
      .then((response) => {
        if (response.status === 200) {
          showToast(t("cancelDebitNote"), "success");
          getActivePageData();
        } else {
          showToast(t("cancelDebitNoteError"), "error");
        }
        setIsLoaderVisible(false);
      })
      .catch((error) => {
        console.error(error);
        showToast(checkErrorMsg(error) || t("bookInterestError"), "error");
        setIsLoaderVisible(false);
      });
  };

  const downloadFile = ({ fileContent, fileName }) => {
    const byteCharacters = atob(fileContent);
    const byteNumbers = new Uint8Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const blob = new Blob([byteNumbers], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleRefreshData = (refreshData) => {
    setIsLoaderVisible(true);
    const fiscalMonthYear =
      refreshData.ibRequestCalculationDTOList &&
      refreshData.ibRequestCalculationDTOList[0] &&
      refreshData.ibRequestCalculationDTOList[0].fiscalMonthYear;
    const companyCode =
      refreshData.ibRequestCalculationDTOList &&
      refreshData.ibRequestCalculationDTOList[0] &&
      refreshData.ibRequestCalculationDTOList[0].companyCode;
    getDataWAuth(IB_SAP_CALLBY_ASSIGNMENT, {
      assignmentNumber: refreshData.assignmentNo,
      fiscalMonthYear: fiscalMonthYear,
      customerNo: refreshData.customerNo,
      companyCode: companyCode,
    })
      .then((res) => {
        if (res?.data && res?.data?.data && res?.data?.data.length > 0) {
          showToast(res.data.message, "success");
          setIsLoaderVisible(false);
          setData((prevData) =>
            prevData.map((row) => {
              if (row.assignmentNo === refreshData.assignmentNo) {
                const updatedRow = { ...row };
                const updatedRequestList = res?.data?.data;
                updatedRow.ibRequestCalculationDTOList = updatedRequestList;
                return updatedRow;
              }
              return row;
            }),
          );
        } else {
          showToast(res?.data && res?.data?.message, "error");
          setIsLoaderVisible(false);
        }
      })
      .catch((error) => {
        showToast(error.message, "error");
        setIsLoaderVisible(false);
      });
  };

  return (
    <div>
      {isLoaderVisible && <PageLoader />}
      {toastManager.toast}
      {modalManager.modal}
      <h2>{"Calculation"}</h2>
      <div className="container">
        <IBFilters
          getSelectedFilters={getSelectedFilters}
          isSchedulerRunning={isSchedulerRunning}
          showToast={showToast}
        />
      </div>
      <div className="container">
        <CalculationNav
          countPerPage={itemsPerPage}
          getActivePage={getActivePage}
          initialActiveItem={requestedPage}
        />
        <CalculationGrid
          data={data}
          selectedPage={requestedPage}
          save={save}
          isBookingActive={isBookingActive}
          handleExcludeClick={handleExcludeClick}
          handleIncludeClick={handleIncludeClick}
          handleDownloadPDF={handleDownloadPDF}
          handleOnBookInterstClick={handleOnBookInterstClick}
          handleCheckboxClick={handleCheckboxClick}
          reasonCodes={reasonCodes}
          schedulerStatus={schedulerStatus}
          handleDownloadLogsClick={handleDownloadLogsClick}
          handleDownloadAllLogsClick={handleDownloadAllLogsClick}
          lastExpandedRowId={lastExpandedRowId}
          handleCancelDebitNoteClick={handleCancelDebitNote}
          handleRefreshData={handleRefreshData}
          country={country}
        />
      </div>
    </div>
  );
}

export default CalculationPage;